import { useEffect, useMemo, useState } from 'react'
import './BooksStatistics.scss'
import BooksService from '../../../services/books.service'
import { Link } from 'react-router-dom'
import { formatDate } from '../../../shared/utils/formatDate'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { filterBooksStatisticsSchema } from '../../../schemas/bookSchema'
import BookChartsCard from '../components/BookChartsCard/BookChartsCard'
import BookChartsTierCard from '../components/BookChartsTierCard/BookChartsTierCard'

const BookStatistics = () => {
  const [books, setBooks] = useState([])
  const [statistics, setStatistics] = useState({
    totalAccesses: 0,
    users: [],
    books: []
  })

  const today = useMemo(() => new Date(), [])
  const sevenDaysAgo = useMemo(
    () => new Date(today).setDate(today.getDate() - 7),
    [today]
  )

  const { register, handleSubmit, formState } = useForm({
    resolver: zodResolver(filterBooksStatisticsSchema),
    defaultValues: {
      startDate: formatDate(sevenDaysAgo, 'yyyy-MM-dd'),
      endDate: formatDate(today, 'yyyy-MM-dd'),
      bookId: ''
    }
  })

  useEffect(() => {
    async function loadBooksAndStatistics() {
      const query = `startDate=${formatDate(
        sevenDaysAgo,
        'yyyy-MM-dd'
      )}&endDate=${formatDate(today, 'yyyy-MM-dd')}`
      const loadedStatistics = await BooksService.getStatistics(query)
      setStatistics(loadedStatistics)

      const loadedBooks = await BooksService.listAll()
      setBooks(loadedBooks)
    }

    loadBooksAndStatistics()
  }, [sevenDaysAgo, today])

  async function handleChartsFilter(data) {
    let query = `startDate=${data.startDate}&endDate=${data.endDate}`
    if (data.bookId !== null) {
      query = query + `&bookId=${data.bookId}`
    }

    const loadedStatistics = await BooksService.getStatistics(query)
    setStatistics(loadedStatistics)
  }

  return (
    <div className='content-card'>
      <div className='row page-title create-book-header'>
        <h1>Estatísticas</h1>
        <Link
          to='/books'
          className='button-full go-back-button'
        >
          Voltar
        </Link>
      </div>

      <div className='statistics-page-header content-header'>
        <h3 className='list-title'>Informações sobre os acessos</h3>
        <div className='charts-filter'>
          <div className='charts-filter-header'>
            <i className='fas fa-filter'></i>
            <h4 className='form-title'>Filtro</h4>
          </div>
          <form
            className='charts-filter-form'
            onSubmit={handleSubmit(handleChartsFilter)}
          >
            <fieldset>
              <label htmlFor='filter-start-date'>A partir de:</label>
              <input
                type='date'
                className='form-control'
                id='filter-start-date'
                {...register('startDate')}
              />
            </fieldset>
            <fieldset>
              <label htmlFor='filter-start-date'>Até:</label>
              <input
                type='date'
                className='form-control'
                id='filter-end-date'
                {...register('endDate')}
              />
            </fieldset>
            <fieldset>
              <label id='filter-book-id'>Livro:</label>
              <select
                className='form-control'
                id='filter-book-id'
                {...register('bookId')}
              >
                <option value=''>Todos os livros</option>
                {books.map((book) => (
                  <option
                    key={book.id}
                    value={book.id}
                  >
                    {book.title}
                  </option>
                ))}
              </select>
            </fieldset>
            <button
              className='button-full btn-save'
              disabled={formState.isSubmitting}
            >
              {formState.isSubmitting ? (
                <i className='fas fa-spinner spinner-animation'></i>
              ) : (
                'Filtrar'
              )}
            </button>
          </form>
        </div>
      </div>

      <div className='charts-container'>
        <div className='charts-numbers-container'>
          <BookChartsCard
            title='Total de visualizações'
            iconClasses='far fa-eye'
            statistic={statistics.totalAccesses}
            description='Acessos no período'
          />
          {statistics.books && (
            <BookChartsCard
              title='Livros ativos'
              iconClasses='fas fa-book-open'
              statistic={statistics.books.length}
              description='Com visualizações no período'
            />
          )}
          <BookChartsCard
            title='Usuários ativos'
            iconClasses='fas fa-user'
            statistic={statistics.users.length}
            description='Acessaram no período'
          />
        </div>
        <div className='charts-tier-container'>
          {statistics.books && (
            <BookChartsTierCard
              title='Livros mais acessados'
              iconClasses='fas fa-book-open'
              type='books'
              data={statistics.books}
            />
          )}
          <BookChartsTierCard
            title='Usuários mais ativos'
            iconClasses='fas fa-user'
            type='users'
            data={statistics.users}
          />
        </div>
      </div>
    </div>
  )
}

export default BookStatistics
